import { useEffect, useState } from "react";
import { Loader2, Bug, Lightbulb, Image as ImageIcon, Send, CornerDownRight } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const STATUSES = [["all", "Alles"], ["open", "Open"], ["in_progress", "In behandeling"], ["closed", "Gesloten"]];
const STATUS_CLS = { open: "bg-blue-100 text-blue-700", in_progress: "bg-amber-100 text-amber-700", closed: "bg-emerald-100 text-emerald-700" };
const TYPE_LABELS = { vraag: "Vraag", suggestie: "Suggestie", support: "Support", bug: "Bug", suggestion: "Suggestie" };

export default function Tickets() {
  const [filter, setFilter] = useState("all");
  const [data, setData] = useState(null);
  const [note, setNote] = useState({});
  const [reply, setReply] = useState({});
  const [replyBusy, setReplyBusy] = useState(null);

  const load = () => { setData(null); api.get("/admin/tickets", { params: { status: filter } }).then((r) => setData(r.data)).catch(() => {}); };
  useEffect(load, [filter]);

  const setStatus = async (tk, status) => {
    await api.patch(`/admin/tickets/${tk.id}`, { status, admin_note: note[tk.id] ?? tk.admin_note ?? "" });
    load();
  };

  const sendReply = async (tk) => {
    const msg = (reply[tk.id] || "").trim();
    if (!msg) return;
    setReplyBusy(tk.id);
    try {
      const r = await api.post(`/admin/tickets/${tk.id}/reply`, { message: msg });
      toast.success(r.data.email_sent ? "Reactie verzonden per e-mail" : "Reactie opgeslagen (e-mail niet verstuurd)");
      setReply({ ...reply, [tk.id]: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Versturen mislukt");
    } finally { setReplyBusy(null); }
  };

  if (!data) return <div className="flex items-center gap-2 text-slate-400 py-12"><Loader2 className="w-5 h-5 animate-spin" /> Laden…</div>;

  return (
    <div data-testid="sa-tickets">
      <div className="flex flex-wrap gap-2 mb-5">
        {STATUSES.map(([id, label]) => (
          <button key={id} data-testid={`ticket-filter-${id}`} onClick={() => setFilter(id)}
            className={`rounded-xl px-3 py-1.5 text-sm font-bold ${filter === id ? "bg-indigo-600 text-white" : "bg-white border border-slate-200 text-slate-500"}`}>
            {label}{id !== "all" && data.counts[id] != null ? ` (${data.counts[id]})` : ""}
          </button>
        ))}
      </div>

      {data.tickets.length === 0 ? <p className="text-slate-400 py-8 text-center">Geen tickets.</p> : (
        <div className="space-y-3">
          {data.tickets.map((tk) => (
            <div key={tk.id} data-testid={`sa-ticket-${tk.id}`} className="bg-white border border-slate-200 rounded-2xl shadow-sm p-4">
              <div className="flex items-start gap-3">
                {tk.type === "bug" ? <Bug className="w-5 h-5 text-rose-500 shrink-0 mt-0.5" /> : <Lightbulb className="w-5 h-5 text-indigo-500 shrink-0 mt-0.5" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-bold">{tk.title}</h3>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${STATUS_CLS[tk.status] || ""}`}>{tk.status}</span>
                    {tk.source === "landing" && <span className="rounded-full px-2 py-0.5 text-xs font-bold bg-violet-100 text-violet-700" data-testid={`ticket-source-${tk.id}`}>Contact · {TYPE_LABELS[tk.type] || tk.type}</span>}
                  </div>
                  <p className="text-sm text-slate-600 mt-1">{tk.description}</p>
                  <p className="text-xs text-slate-400 mt-1">{tk.name} · {tk.email || "—"} · {tk.org_name || "—"} · {tk.created_at ? new Date(tk.created_at).toLocaleString("nl-NL") : ""}</p>
                  {tk.screenshots?.length > 0 && (
                    <div className="flex gap-2 mt-2 flex-wrap">
                      {tk.screenshots.map((s, i) => (
                        <a key={i} href={s} target="_blank" rel="noreferrer" className="block w-20 h-20 rounded-lg border border-slate-200 overflow-hidden bg-slate-50">
                          <img src={s} alt="screenshot" className="w-full h-full object-cover" />
                        </a>
                      ))}
                    </div>
                  )}
                  <div className="mt-3 flex flex-col sm:flex-row gap-2">
                    <input defaultValue={tk.admin_note} onChange={(e) => setNote({ ...note, [tk.id]: e.target.value })}
                      placeholder="Interne notitie / reactie…" className="flex-1 border border-slate-200 rounded-lg px-3 py-1.5 text-sm" />
                    <div className="flex gap-1.5">
                      {[["open", "Open"], ["in_progress", "In behandeling"], ["closed", "Gesloten"]].map(([id, label]) => (
                        <button key={id} data-testid={`sa-ticket-set-${id}`} onClick={() => setStatus(tk, id)}
                          className={`rounded-lg px-2.5 py-1.5 text-xs font-bold ${tk.status === id ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{label}</button>
                      ))}
                    </div>
                  </div>

                  {tk.replies?.length > 0 && (
                    <div className="mt-3 space-y-1.5" data-testid={`sa-ticket-replies-${tk.id}`}>
                      {tk.replies.map((rp, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
                          <CornerDownRight className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                          <div className="min-w-0">
                            <p className="text-slate-700">{rp.message}</p>
                            <p className="text-[11px] text-slate-400 mt-0.5">{rp.by} · {rp.at ? new Date(rp.at).toLocaleString("nl-NL") : ""}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="mt-3 border-t border-slate-100 pt-3">
                    {tk.email ? (
                      <div className="flex flex-col sm:flex-row gap-2">
                        <input value={reply[tk.id] || ""} onChange={(e) => setReply({ ...reply, [tk.id]: e.target.value })}
                          data-testid={`sa-ticket-reply-input-${tk.id}`}
                          onKeyDown={(e) => e.key === "Enter" && sendReply(tk)}
                          placeholder={`Antwoord mailen naar ${tk.email}…`}
                          className="flex-1 border border-slate-200 rounded-lg px-3 py-1.5 text-sm" />
                        <button data-testid={`sa-ticket-reply-send-${tk.id}`} onClick={() => sendReply(tk)} disabled={replyBusy === tk.id || !(reply[tk.id] || "").trim()}
                          className="inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50">
                          {replyBusy === tk.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}Beantwoorden
                        </button>
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400">Geen e-mailadres bekend — beantwoorden per e-mail niet mogelijk.</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
